import { useImpersonation } from '@shared/contexts/ImpersonationContext';
import { useTranslation } from 'react-i18next';

/**
 * Feature 709: Additional warning banner for sensitive pages during impersonation.
 *
 * This component displays a prominent warning banner on sensitive pages (settings, billing)
 * when an admin is impersonating a user. The banner reminds the admin that sensitive
 * actions are disabled for security reasons.
 *
 * @example
 * ```tsx
 * import ImpersonationSensitiveBanner from '@shared/components/ImpersonationSensitiveBanner';
 *
 * export default function Settings() {
 *   return (
 *     <div>
 *       <ImpersonationSensitiveBanner />
 *       <SettingsContent />
 *     </div>
 *   );
 * }
 * ```
 */
export default function ImpersonationSensitiveBanner() {
  const { t } = useTranslation('common');
  const { isImpersonating } = useImpersonation();

  // Only show during impersonation
  if (!isImpersonating) {
    return null;
  }

  return (
    <div
      className="border-b-4 bg-state-danger-bg-light border-state-danger-light/40 mx-4 mt-4 rounded-md shadow-sm dark:bg-state-danger-bg-dark dark:border-state-danger-dark/40"
      role="alert"
      aria-live="polite"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-state-danger-light dark:text-state-danger-dark"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-state-danger-light dark:text-state-danger-dark">
            {t('sensitiveBanner.title')}
          </h3>
          <div className="mt-2 text-sm text-state-danger-light dark:text-state-danger-dark">
            <p>{t('sensitiveBanner.message')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
